import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  IconButton,
  Box,
  Alert,
  CircularProgress,
  Switch,
  Chip,
} from '@mui/material';
import { Close as CloseIcon, ArrowBack as ArrowBackIcon, SearchRounded } from '@mui/icons-material';
import { ReactComponent as ToolIcon } from 'new/components/icons/tool.svg';
import { TextField } from 'new/elements/TextField';
import { ChipBadge } from 'new/elements/ChipBadge';
import CardsListPageNumbers from 'js/components/elements/cards/CardsListPageNumbers';
import BaseCard from '../../components/shared/BaseCard/BaseCard';
import { useGetToolsQuery } from '../../state/aiApi';
import type { ApiTool } from '../../state/aiApiTypes';
import { getStatusLabel, getToolKindLabel, getToolDisplayName } from '../../utils/toolLabels';
import { getStatusChipColor } from 'new/modules/ai/utils/statusColorHelpers';
import { useListFiltering } from '../../hooks/useListFiltering';
import {
  containsMarketplaceTool,
  defaultAccessScopeFor,
  type ToolsetAccessScope,
} from '../../utils/toolsetAccessScope';
import { extractApiError } from '../../utils/errorUtils';

interface CreateToolsetDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    description?: string;
    tool_ids: number[];
    mcp_gateway_enabled?: boolean;
    access_scope?: ToolsetAccessScope;
  }) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
}

type Step = 'select-tools' | 'configure';

const EMPTY_TOOLS: ApiTool[] = [];

const CreateToolsetDialog: React.FC<CreateToolsetDialogProps> = ({
  open,
  onClose,
  onSubmit,
  isLoading = false,
  error = null,
}) => {
  const [step, setStep] = useState<Step>('select-tools');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [enableGateway, setEnableGateway] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    data: tools = EMPTY_TOOLS,
    isLoading: toolsLoading,
    error: toolsError,
  } = useGetToolsQuery(undefined, { skip: !open });

  const {
    searchQuery,
    setSearchQuery,
    paginatedItems: paginatedTools,
    totalItems,
    totalPages,
    safePage,
    handlePageChange,
  } = useListFiltering({
    items: tools,
    searchFields: (t) => [t.name, t.description, t.key, String(t.id)],
  });

  const selectedTools = useMemo(
    () => tools.filter((t) => selectedIds.has(t.id)),
    [tools, selectedIds],
  );

  // A Data Product cannot be delegated org-wide by someone who was merely
  // granted it, so such a toolset is created private. See defaultAccessScopeFor.
  const hasMarketplaceTool = useMemo(() => containsMarketplaceTool(selectedTools), [selectedTools]);

  const toggle = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleClose = () => {
    if (isLoading) return;
    setStep('select-tools');
    setSelectedIds(new Set());
    setName('');
    setDescription('');
    setEnableGateway(false);
    setSubmitError(null);
    onClose();
  };

  const handleNext = () => {
    if (selectedTools.length === 0) return;
    if (!name) {
      const autoName =
        selectedTools.length === 1
          ? `${getToolDisplayName(selectedTools[0])} Toolset`
          : `Toolset (${selectedTools.length} tools)`;
      setName(autoName);
    }
    setStep('configure');
  };

  const handleBack = () => {
    setStep('select-tools');
    setSubmitError(null);
  };

  const handleSubmit = async () => {
    if (!name.trim() || selectedTools.length === 0) return;
    setSubmitError(null);
    try {
      await onSubmit({
        name: name.trim(),
        description: description.trim() || undefined,
        tool_ids: selectedTools.map((t) => t.id),
        mcp_gateway_enabled: enableGateway || undefined,
        access_scope: defaultAccessScopeFor(selectedTools),
      });
      handleClose();
    } catch (err: any) {
      setSubmitError(err?.message || 'Failed to create toolset');
    }
  };

  const displayError = error || submitError;
  const loadToolsError = toolsError ? extractApiError(toolsError, 'Failed to load tools') : null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { height: '80vh', maxHeight: 700 } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {step === 'configure' && (
            <IconButton size="small" onClick={handleBack} disabled={isLoading}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h6">
            {step === 'select-tools' ? 'Select Tools' : 'Configure Toolset'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Step {step === 'select-tools' ? '1' : '2'} of 2
          </Typography>
        </Stack>
        <IconButton size="small" onClick={handleClose} disabled={isLoading}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {displayError && (
          <Alert severity="error" sx={{ mx: 3, mt: 2 }} onClose={() => setSubmitError(null)}>
            {displayError}
          </Alert>
        )}

        {step === 'select-tools' && (
          <Box sx={{ flex: 1, p: 3, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select one or more tools to include in your toolset. An MCP server will be created for
              the toolset.
            </Typography>

            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
              <TextField
                placeholder="Search by name, description, ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                startAdornment={<SearchRounded fontSize="small" color="disabled" />}
                sx={{ flex: 1 }}
              />
              <CardsListPageNumbers
                isLoading={toolsLoading}
                currentPage={safePage}
                totalPages={totalPages}
                totalItems={totalItems}
                onListingChange={handlePageChange}
                disableHistoryUpdate
              />
            </Stack>

            <Box sx={{ flex: 1, overflowY: 'auto', mx: -0.5, px: 0.5 }}>
              {toolsLoading ? (
                <Stack alignItems="center" justifyContent="center" height="100%">
                  <CircularProgress size={24} />
                </Stack>
              ) : loadToolsError ? (
                <Stack alignItems="center" justifyContent="center" height="100%">
                  <Typography variant="body2" color="error">
                    {loadToolsError}
                  </Typography>
                </Stack>
              ) : paginatedTools.length === 0 ? (
                <Stack alignItems="center" justifyContent="center" height="100%">
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery ? 'No tools match your search criteria.' : 'No tools available.'}
                  </Typography>
                </Stack>
              ) : (
                <Stack
                  spacing={1}
                  sx={{ '& .MuiCardContent-root': { p: 0, '&:last-child': { pb: 0 } } }}
                >
                  {paginatedTools.map((tool) => (
                    <BaseCard
                      key={tool.id}
                      variant="mui"
                      icon={<ToolIcon />}
                      selectable
                      selected={selectedIds.has(tool.id)}
                      onSelect={() => toggle(tool.id)}
                      infoButton={false}
                      title={
                        <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {getToolDisplayName(tool)}
                          </Typography>
                          {tool.kind && (
                            <Chip
                              label={getToolKindLabel(tool.kind)}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: 11 }}
                            />
                          )}
                        </Stack>
                      }
                      subtitle={
                        tool.description ? (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {tool.description}
                          </Typography>
                        ) : undefined
                      }
                      badges={[
                        tool.status ? (
                          <ChipBadge
                            key="status"
                            label={getStatusLabel(tool.status)}
                            color={getStatusChipColor(tool.status)}
                          />
                        ) : null,
                        <ChipBadge key="id" color="disabled" label={`ID ${tool.id}`} />,
                      ].filter(Boolean)}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          </Box>
        )}

        {step === 'configure' && (
          <Box sx={{ flex: 1, p: 3 }}>
            <Stack spacing={3}>
              <Typography variant="body2" color="text.secondary">
                Configure your toolset. {selectedTools.length} Tool
                {selectedTools.length !== 1 ? 's' : ''} selected.
              </Typography>

              <TextField
                label="Toolset Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter a name for your toolset"
                required
                error={!name.trim()}
                helperText={!name.trim() ? 'Name is required' : ''}
              />

              <TextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this toolset does (optional)"
                multiline
                rows={3}
              />

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle2">Enable MCP Gateway</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Connect external MCP servers and access a unified tool catalog
                  </Typography>
                </Box>
                <Switch
                  checked={enableGateway}
                  onChange={(e) => setEnableGateway(e.target.checked)}
                  size="small"
                />
              </Stack>

              {hasMarketplaceTool && (
                <Alert severity="info">
                  This toolset will be private because it includes a Data Product. Sharing one with
                  your whole organization requires admin access on the source Nexset — ask its owner
                  if you need a shared toolset.
                </Alert>
              )}

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Selected Tools ({selectedTools.length})
                </Typography>
                <Stack spacing={0.5}>
                  {selectedTools.map((tool) => (
                    <Typography key={tool.id} variant="body2" color="text.secondary">
                      {getToolDisplayName(tool)} (ID: {tool.id})
                    </Typography>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        {step === 'select-tools' ? (
          <Button variant="contained" onClick={handleNext} disabled={selectedTools.length === 0}>
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading || !name.trim()}
            startIcon={isLoading ? <CircularProgress size={16} /> : null}
          >
            {isLoading ? 'Creating...' : 'Create Toolset'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateToolsetDialog;
